import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Mic, FileText, ShieldCheck, Clock, Check, Loader2, ArrowRight,
} from "lucide-react";
import logoFull from "@/assets/logo-full.png";

const FEATURES = [
  { icon: Search, title: "Deep public-web scan", body: "Three grounded passes across PRCA/CPRA, podcasts, news, socials, sponsors and horse databases — real sources, no made-up facts." },
  { icon: Mic, title: "Presenter brief", body: "5 Things To Know, conversation starters and fresh questions — written for the mic, every fact linked to its source." },
  { icon: FileText, title: "One-click run sheets", body: "Build a run sheet across any events and export a broadcast-ready Word doc in your program style." },
  { icon: ShieldCheck, title: "Confidence-graded", body: "Every nugget is tagged Official, athlete's own, established media, third-party or unverified." },
];

export default function Landing() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0D11] text-[#F3EFE6]">
      {/* Top bar */}
      <header className="border-b border-[#1E212A] bg-[#0B0D11]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-28 flex items-center justify-between">
          <img src={logoFull} alt="Rodeo Intel" className="h-24 w-auto" />
          <Link to="/app" data-testid="landing-launch" className="text-sm font-semibold text-[#E8833A] hover:text-[#F0924A] flex items-center gap-1">
            Launch app <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-16 text-center">
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Walk up to any cowboy or cowgirl
          <span className="text-[#E8833A]"> already knowing their story.</span>
        </h1>
        <p className="text-[#B8BAC4] text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
          Rodeo Intel scans the public web and builds a sourced interview brief on every rodeo athlete —
          cowboys and cowgirls alike — family, horses, road life, injuries, podcasts and standings — so announcers never run out of things to say.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link to="/app" data-testid="hero-explore" className="px-6 py-3 rounded-lg font-semibold bg-[#1A1D26] border border-[#262933] hover:border-[#E8833A]/50 transition-colors">
            Explore the app
          </Link>
          <a href="#pricing" className="px-6 py-3 rounded-lg font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
            See pricing
          </a>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {FEATURES.map((f) => (
          <div key={f.title} className="rounded-xl border border-[#262933] bg-[#12141A] p-6">
            <f.icon className="h-6 w-6 text-[#E8833A] mb-3" />
            <h3 className="font-display font-bold text-lg">{f.title}</h3>
            <p className="text-[#9A9CA6] text-sm mt-2 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-3xl mx-auto px-5 py-16">
        <h2 className="font-display font-extrabold text-3xl text-center mb-2">Simple pricing</h2>
        <p className="text-[#9A9CA6] text-center mb-10">Unlimited access to the shared intel pool. Pay only when you run fresh scans.</p>

        <div className="rounded-2xl border-2 border-[#E8833A]/40 bg-[#12141A] p-8" data-testid="pricing-pro">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A]">Pro</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display font-extrabold text-5xl">$50</span>
            <span className="text-[#9A9CA6]">/ month</span>
          </div>
          <ul className="mt-6 space-y-3 text-sm">
            {["50 fresh athlete scans included each month",
              "Unlimited viewing of every athlete already in the pool",
              "Presenter briefs, run sheets & Word export",
              "PRCA + CPRA rosters, standings and stock"].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-[#D8DAE2]">
                <Check className="h-4 w-4 text-[#4ADE80] mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <button
            data-testid="subscribe-pro-button"
            onClick={() => nav("/login")}
            className="mt-7 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors"
          >
            Get started
            <ArrowRight className="h-4 w-4" />
          </button>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-[#686B77]">
            <Clock className="h-3.5 w-3.5" /> Create your account, then the organizer activates your credits.
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1E212A] py-8 text-center text-xs text-[#686B77]">
        Rodeo Intel · Athlete Interview Intelligence
      </footer>
    </div>
  );
}
